import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useAuth } from '@/shared/auth'
import { getAuthErrorMessage } from '@/shared/lib/auth-errors'
import { Button } from '@/shared/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/card'
import { Input } from '@/shared/ui/input'
import { Label } from '@/shared/ui/label'
import { LanguageSwitchButton } from '@/shared/ui/language-switch-button'

interface PasswordRecoveryFormProps extends React.ComponentPropsWithoutRef<'div'> {
  onBackToLogin?: () => void
  onSwitchToSignup?: () => void
}

const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

export function PasswordRecoveryForm({ className, onBackToLogin, onSwitchToSignup, ...props }: PasswordRecoveryFormProps) {
  const { t } = useTranslation()
  const { sendPasswordReset } = useAuth()
  const [email, setEmail] = useState('')
  const [error, setError] = useState('')
  const [emailFormatError, setEmailFormatError] = useState(false)
  const [success, setSuccess] = useState(false)
  const [userExists, setUserExists] = useState<boolean | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setEmailFormatError(false)
    setSuccess(false)
    setUserExists(null)

    if (!validateEmail(email)) {
      setEmailFormatError(true)
      return
    }

    setIsLoading(true)

    try {
      const result = await sendPasswordReset(email)
      const data = result.data as { success: boolean; userExists?: boolean; emailSent?: boolean }

      if (data.userExists === false) {
        setUserExists(false)
        setSuccess(true)
      } else if (data.userExists === true && data.emailSent) {
        setUserExists(true)
        setSuccess(true)
      } else {
        setSuccess(true)
      }
    } catch (err: unknown) {
      setError(getAuthErrorMessage(err, t, 'passwordRecovery'))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className={className} {...props}>
      <div className='absolute right-4 top-4'>
        <LanguageSwitchButton />
      </div>
      <Card>
        <CardHeader>
          <CardTitle className='text-2xl'>
            {t('auth.passwordRecovery.title', 'Recuperar Contraseña')}
          </CardTitle>
          <CardDescription>
            {t(
              'auth.passwordRecovery.description',
              'Ingresa tu email y te enviaremos un enlace para restablecer tu contraseña',
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <div className='flex flex-col gap-6'>
              {error && <div className='rounded-md bg-red-50 p-3 text-sm text-red-800'>{error}</div>}
              {emailFormatError && (
                <div className='rounded-md bg-red-50 p-4 text-sm text-red-800'>
                  <p className='mb-2 font-semibold'>
                    {t('auth.errors.invalidEmail', 'Email inválido')}
                  </p>
                  <p>
                    {t('auth.passwordRecovery.invalidEmailMessage', 'Por favor, ingresa un email válido.')}
                  </p>
                </div>
              )}
              {success && userExists === false && (
                <div className='rounded-md bg-yellow-50 p-4 text-sm text-yellow-800'>
                  <p className='mb-2 font-semibold'>
                    {t('auth.passwordRecovery.userNotFound', 'No existe cuenta asociada a este email')}
                  </p>
                  <p>
                    {t('auth.passwordRecovery.userNotFoundMessage', 'No se encontró ninguna cuenta asociada a este email.')}{' '}
                    {onSwitchToSignup && (
                      <>
                        {t('auth.passwordRecovery.suggestSignup', '¿Quieres')}{' '}
                        <button
                          type='button'
                          onClick={onSwitchToSignup}
                          className='font-semibold underline underline-offset-4 hover:no-underline'
                        >
                          {t('auth.passwordRecovery.signupLink', 'registrarte aquí')}
                        </button>
                        ?
                      </>
                    )}
                  </p>
                </div>
              )}
              {success && userExists === true && (
                <div className='rounded-md bg-green-50 p-4 text-sm text-green-800'>
                  <p className='mb-2 font-semibold'>
                    {t('auth.passwordRecovery.success', 'Email enviado')}
                  </p>
                  <p>
                    {t(
                      'auth.passwordRecovery.successMessage',
                      'Recibirás un enlace para restablecer tu contraseña.',
                    )}
                  </p>
                </div>
              )}
              <div className='grid gap-2'>
                <Label htmlFor='email'>{t('auth.passwordRecovery.email', 'Email')}</Label>
                <Input
                  id='email'
                  type='email'
                  placeholder={t('auth.passwordRecovery.emailPlaceholder', 'tu@email.com')}
                  value={email}
                  onChange={e => {
                    setEmail(e.target.value)
                    if (emailFormatError) {
                      setEmailFormatError(false)
                    }
                  }}
                  required
                  disabled={isLoading}
                />
              </div>
              <Button type='submit' className='w-full' disabled={isLoading}>
                {isLoading
                  ? t('auth.passwordRecovery.loading', 'Enviando...')
                  : t('auth.passwordRecovery.submit', 'Enviar enlace de recuperación')}
              </Button>
              <button
                type='button'
                onClick={onBackToLogin}
                className='text-center text-sm underline underline-offset-4'
              >
                {t('auth.passwordRecovery.backToLogin', 'Volver al inicio de sesión')}
              </button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
